import { graphql } from 'babel-plugin-relay/macro'
import type { FC } from 'react'
import React, { useEffect } from 'react'
import { useLazyLoadQuery } from 'react-relay-offline'
import { useIntl } from 'react-intl'
import { Tab, useTabState, csx, TabList, Text } from '@vtex/admin-ui'
import type { StyleProp } from '@vtex/admin-ui'
import { isEmpty } from 'ramda'

import type { TurnListQuery } from './__generated__/TurnListQuery.graphql'
import { TurnListProvider } from './context/TurnListProvider'
import { CustomTabContent } from './components/CustomTabContent'
import { SalesAssociateTraking } from './components/SalesAssociateTraking'
import { Rankings } from './components/Rankings'
import { Campaigns } from './components/Campaigns'
import {
  TopbarLeftArea,
  TopbarRightArea,
} from '../Instore/components/Header/Topbar'
import { logger } from '../../logger'
import { environment } from '../../core/components'
import { useSalesAssociate } from '../../core/globalState'
import { ReadableMessageError } from '../../core/screens/GenericError/ReadableMessageError'
import { GoalsAuthorizationModal } from './components/Goals/GoalsAuthorizationModal'

const TURN_LIST_QUERY = graphql`
  query TurnListQuery($masterdataId: String!) {
    salesAssociates(
      where: {
        store: { masterdataId: { eq: $masterdataId } }
        active: { eq: true }
      }
      first: 50
    ) {
      ...TurnListProvider_salesAssociatesConnection
    }
    salesAssociateTrackingMovementFlows(
      where: { store: { masterdataId: { eq: $masterdataId } } }
    ) {
      edges {
        node {
          ...TurnListProvider_movement
        }
      }
    }
    salesAssociateTrackingServiceFlow(
      where: { store: { masterdataId: { eq: $masterdataId } } }
    ) {
      ...TurnListProvider_salesAssociateTrackingFlow
    }
  }
`

const tabStyles: StyleProp = {
  paddingX: '$space-5',
  flex: 1,
}

const tabListStyles: StyleProp = {
  textTransform: 'none',
  border: '$neutral',
  flex: '1 !important',
  width: '100%',
}

enum TabListEnum {
  trackingList = 'trackingList',
  campaigns = 'campaigns',
  rankings = 'rankings',
}

const TurnListContent: FC = () => {
  const intl = useIntl()
  const tab = useTabState()
  const { salesAssociate: currentSalesAssociate } = useSalesAssociate()

  const { data, isLoading } = useLazyLoadQuery<TurnListQuery>(
    TURN_LIST_QUERY,
    {
      masterdataId: currentSalesAssociate?.store ?? '',
    },
    {
      fetchPolicy: 'store-or-network',
      networkCacheConfig: {
        ttl: 7 * 24 * 60 * 60 * 1000 /* 7 days */,
      },
    }
  )

  useEffect(
    function turnListDataChanged() {
      logger.info({
        message: 'Turn list data changed',
        workflowType: 'store-data-changed',
        context: 'turnlist-page',
        data: {
          hasData: data && !isEmpty(data),
          isLoading,
        },
      })
    },
    [data, isLoading]
  )

  const initialMovementFlow =
    data?.salesAssociateTrackingMovementFlows?.edges?.[0]?.node

  if (!isLoading) {
    if (!data?.salesAssociateTrackingServiceFlow || !initialMovementFlow)
      throw new ReadableMessageError('store-not-found')
  }

  if (
    isLoading ||
    !data?.salesAssociates ||
    !data?.salesAssociateTrackingMovementFlows ||
    !initialMovementFlow ||
    !data?.salesAssociateTrackingServiceFlow
  ) {
    return null
  }

  return (
    <TurnListProvider
      salesAssociatesConnectionRef={data.salesAssociates}
      movementRef={initialMovementFlow}
      salesAssociateTrackingServiceFlowRef={
        data.salesAssociateTrackingServiceFlow
      }
    >
      <TopbarRightArea>
        <div
          className={csx({
            display: 'flex',
            float: 'right',
          })}
        >
          <GoalsAuthorizationModal />
        </div>
      </TopbarRightArea>
      <TabList state={tab} className={csx({ ...tabListStyles })}>
        <Tab
          className={csx({
            ...tabStyles,
          })}
          id={TabListEnum.trackingList}
          aria-label={intl.formatMessage({ id: 'trackingList' })}
        >
          {intl.messages.trackingList as string}
        </Tab>
        <Tab
          className={csx({
            ...tabStyles,
          })}
          id={TabListEnum.campaigns}
          aria-label={intl.formatMessage({ id: 'campaigns' })}
        >
          {intl.messages.campaigns as string}
        </Tab>
        <Tab
          className={csx({
            ...tabStyles,
          })}
          id={TabListEnum.rankings}
          aria-label={intl.formatMessage({ id: 'rankings' })}
        >
          {intl.messages.rankings as string}
        </Tab>
      </TabList>

      <CustomTabContent
        id={TabListEnum.trackingList}
        state={tab}
        renderOnlyFocused={false}
      >
        <SalesAssociateTraking />
      </CustomTabContent>
      <CustomTabContent id={TabListEnum.campaigns} state={tab}>
        <Campaigns />
      </CustomTabContent>
      <CustomTabContent id={TabListEnum.rankings} state={tab}>
        <Rankings />
      </CustomTabContent>
    </TurnListProvider>
  )
}

export const TurnListScreen = () => {
  const intl = useIntl()

  useEffect(() => {
    if (!environment.isOnline()) return

    environment
      .getStoreOffline()
      .process()
      // eslint-disable-next-line promise/prefer-await-to-then
      .catch((error) => {
        logger.error({
          message:
            'Failure at retrying to process relay offline queue of mutations',
          context: 'relay-offline-at-turn-list',
          error: error instanceof Error ? error : new Error(String(error)),
        })
      })
  }, [])

  return (
    <>
      <TopbarLeftArea>
        <Text variant="title2">
          {intl.formatMessage({ id: 'menu-turnlist' })}
        </Text>
      </TopbarLeftArea>
      <TurnListContent />
    </>
  )
}
